import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert, Modal, message } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getOpportunity, deleteOpportunity, type Opportunity } from '@/api/opportunityApi';
import { getPipeline, getStage, type Pipeline, type PipelineStage } from '@/api/pipelineApi';
import { getCustomer, type Customer } from '@/api/customerApi';
import { getContact, type Contact } from '@/api/contactApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  won: 'green',
  lost: 'red',
  suspended: 'orange',
};

export default function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolved names
  const [customerName, setCustomerName] = useState<string>('');
  const [contactName, setContactName] = useState<string>('');
  const [pipelineName, setPipelineName] = useState<string>('');
  const [stageName, setStageName] = useState<string>('');

  const canWrite = hasPermission('opportunities', 'write');
  const canDelete = hasPermission('opportunities', 'delete');

  const resolveNames = useCallback(async (opp: Opportunity) => {
    // Resolve all names in parallel, failing silently for each
    const [customer, contact, pipeline, stage] = await Promise.allSettled([
      opp.customer_id ? getCustomer(opp.customer_id) : Promise.reject('none'),
      opp.contact_id ? getContact(opp.contact_id) : Promise.reject('none'),
      opp.pipeline_id ? getPipeline(opp.pipeline_id) : Promise.reject('none'),
      opp.stage_id ? getStage(opp.stage_id) : Promise.reject('none'),
    ]);

    if (customer.status === 'fulfilled') setCustomerName((customer.value as Customer).name);
    if (contact.status === 'fulfilled') setContactName((contact.value as Contact).name);
    if (pipeline.status === 'fulfilled') setPipelineName((pipeline.value as Pipeline).name);
    if (stage.status === 'fulfilled') setStageName((stage.value as PipelineStage).name);
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getOpportunity(id)
      .then((opp) => {
        setOpportunity(opp);
        resolveNames(opp);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t, resolveNames]);

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('opportunities.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteOpportunity(id);
          message.success(t('opportunities.delete_success'));
          navigate('/opportunities');
        } catch (err) {
          const msg = err instanceof Error ? err.message : t('app.error');
          message.error(msg);
        }
      },
    });
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error || !opportunity) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/opportunities')}>
          {t('app.back')}
        </Button>
        <Alert type="error" message={error || t('app.not_found')} style={{ marginTop: 16 }} />
      </div>
    );
  }

  const formatDate = (val: string | null) =>
    val ? new Date(val).toLocaleString() : '-';

  const formatDateOnly = (val: string | null) =>
    val ? new Date(val).toLocaleDateString() : '-';

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/opportunities')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/opportunities/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{t('opportunities.detail_title')}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('opportunities.field.name')}>{opportunity.name}</Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.status')}>
          <Tag color={STATUS_COLORS[opportunity.status] ?? 'default'}>
            {t(`opportunities.status_options.${opportunity.status}`, opportunity.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.amount')}>
          {opportunity.amount > 0
            ? `${opportunity.currency || 'TWD'} ${opportunity.amount.toLocaleString()}`
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.probability')}>
          {opportunity.probability}%
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.close_date')}>
          {formatDateOnly(opportunity.close_date)}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.currency')}>
          {opportunity.currency || 'TWD'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.pipeline_id')}>
          {pipelineName || opportunity.pipeline_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.stage_id')}>
          {stageName || opportunity.stage_id}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.customer_id')}>
          {customerName ? (
            <a onClick={() => navigate(`/customers/${opportunity.customer_id}`)}>{customerName}</a>
          ) : (
            opportunity.customer_id || '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.contact_id')}>
          {contactName ? (
            <a onClick={() => navigate(`/contacts/${opportunity.contact_id}`)}>{contactName}</a>
          ) : (
            opportunity.contact_id || '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.owner_id')}>
          {opportunity.owner_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.source')}>
          {opportunity.source || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.description')} span={2}>
          {opportunity.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.tags')}>
          {(opportunity.tags?.length ?? 0) > 0
            ? opportunity.tags?.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.branch_id')}>
          {opportunity.branch_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.notes')} span={2}>
          {opportunity.notes || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.created_by')}>
          {opportunity.created_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.created_at')}>
          {formatDate(opportunity.created_at)}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.updated_by')}>
          {opportunity.updated_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('opportunities.field.updated_at')}>
          {formatDate(opportunity.updated_at)}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
